import { useEffect, useRef } from 'react'

/**
 * Floating scrollbar thumb for containers whose native scrollbar is hidden
 * with the `lav-overlay-scroll` class (see index.css). Windows browsers draw
 * classic scrollbars that are always visible and reserve layout width; this
 * pair recreates the macOS overlay behavior instead — no gutter, and the
 * thumb only fades in while the user is actually scrolling.
 *
 * Must be rendered as a direct child of the scroll container.
 */
export default function OverlayScrollThumb() {
	const thumbRef = useRef(null)

	useEffect(() => {
		const thumb = thumbRef.current
		const scroller = thumb?.parentElement
		if (!scroller) return

		let hideTimer = null
		const onScroll = () => {
			const { scrollTop, scrollHeight, clientHeight } = scroller
			const maxScroll = scrollHeight - clientHeight
			if (maxScroll <= 1) return
			const height = Math.max((clientHeight / scrollHeight) * clientHeight, 28)
			// The thumb is absolutely positioned inside the scrolling content, so
			// its `top` has to carry scrollTop to stay pinned in the viewport.
			const top = scrollTop + (scrollTop / maxScroll) * (clientHeight - height)
			thumb.style.height = `${height}px`
			thumb.style.top = `${top}px`
			thumb.style.opacity = '1'
			clearTimeout(hideTimer)
			hideTimer = setTimeout(() => { thumb.style.opacity = '0' }, 700)
		}

		scroller.addEventListener('scroll', onScroll, { passive: true })
		return () => {
			scroller.removeEventListener('scroll', onScroll)
			clearTimeout(hideTimer)
		}
	}, [])

	return <div ref={thumbRef} className="lav-overlay-thumb" aria-hidden="true" />
}
